import React from 'react';
import { Link } from 'react-router-dom';
import GuideLayout from '../common/GuideLayout';
import GuideCta, { FreeNote } from '../common/GuideCta';
import TemplateCard from '../common/TemplateCard';
import RelatedGuides from '../common/RelatedGuides';
import { relatedTo } from '../common/rsvpCluster';
import { SITE_SIGNUP_URL } from '../signup/startIntent';
import shareImage from '../../../assets/rsvp-qr-partage.webp';
import shotInvitation from '../../../assets/rsvp-1-invitation.webp';
import { FAQ, PHRASES, PLACEMENT, READING_MINUTES, STEPS, TEST, TOC, UPDATED, WHY } from './content';

// Page « QR code faire-part de mariage » : pourquoi, comment le créer, où le
// placer, quelle phrase, l'expiration, le test ; appels à créer sa page et son QR.
const QrGuidePage: React.FC = () => (
  <GuideLayout
    eyebrow="Guide · Faire-part"
    title="QR code gratuit pour votre faire-part de mariage"
    updated={UPDATED}
    published="2026-10-15"
    readingMinutes={READING_MINUTES}
    lead="Un QR code sur le faire-part, et vos invités répondent en un scan depuis leur téléphone : plus de carton réponse ni de timbre. Comment le créer gratuitement, où le placer, quelle phrase écrire à côté, et comment éviter les QR codes qui cessent de fonctionner."
    topCta={
      <GuideCta
        to={SITE_SIGNUP_URL}
        title="Votre QR code de mariage, gratuit"
        text="Créez votre page de réponse en deux minutes et téléchargez son QR code, prêt à imprimer. Il n’expire jamais."
        label="Créer mon QR code gratuit"
      />
    }
    toc={TOC}
    faq={FAQ}
  >
    <section id="pourquoi">
      <h2>Pourquoi un QR code sur le faire-part ?</h2>
      <p>
        Le QR code relie votre faire-part papier à une page en ligne : vos invités le scannent avec l’appareil photo de
        leur téléphone et arrivent directement sur votre page d’invitation, où ils peuvent répondre.
      </p>
      <div className="mg-grid">
        {WHY.map((w) => (
          <div key={w.title} className="mg-card">
            <h3>{w.title}</h3>
            <p>{w.text}</p>
          </div>
        ))}
      </div>
    </section>

    <section id="creer">
      <h2>Créer son QR code de mariage en 4 étapes</h2>
      <ol className="mg-steps">
        {STEPS.map((s) => (
          <li key={s.title}>
            <h3>{s.title}</h3>
            <p>{s.text}</p>
          </li>
        ))}
      </ol>
      <figure className="mg-shot-small">
        <img
          src={shareImage}
          width={900}
          height={351}
          loading="lazy"
          decoding="async"
          alt="Partage de la page d’invitation : copier le lien ou télécharger le QR code à imprimer sur le faire-part"
        />
        <figcaption>Dans votre espace : le lien à copier et le QR code à télécharger.</figcaption>
      </figure>
    </section>

    <section id="placer">
      <h2>Taille, emplacement et couleurs</h2>
      <ul className="mg-bullets">
        {PLACEMENT.map((tip) => (
          <li key={tip}>{tip}</li>
        ))}
      </ul>
    </section>

    <section id="phrases">
      <h2>Quelle phrase écrire à côté du QR code ?</h2>
      <p>Une phrase courte suffit : ce qu’il faut faire, et avant quand. Remplacez ce qui est entre crochets.</p>
      <div className="mg-templates">
        {PHRASES.map((p) => (
          <TemplateCard key={p.title} template={p} />
        ))}
      </div>
    </section>

    <section id="expiration">
      <h2>Attention aux QR codes qui expirent</h2>
      <p>
        Beaucoup de générateurs de QR code « gratuits » créent un code <b>dynamique</b> : il mène d’abord vers leur
        propre lien, qui vous redirige ensuite vers votre site. Pratique pour eux, risqué pour vous : au bout de
        quelques semaines, ce lien peut être désactivé ou devenir payant. Résultat, un QR code imprimé sur 100
        faire-part qui ne mène plus nulle part.
      </p>
      <p>
        Un QR code <b>statique</b>, qui contient directement l’adresse de votre site, n’expire jamais. C’est le cas de
        celui de votre page RSVP Please.
      </p>
    </section>

    <figure className="mg-cta-figure">
      <img
        src={shotInvitation}
        width={420}
        height={909}
        loading="lazy"
        decoding="async"
        alt="La page d’invitation sur laquelle arrivent les invités après avoir scanné le QR code"
      />
      <figcaption>
        <p className="mg-cta-title">Ce que vos invités voient en scannant</p>
        <p>
          Une page d’invitation à vos prénoms, avec votre photo et vos couleurs. Ils répondent pour chaque événement,
          choisissent leur menu, et peuvent répondre pour toute leur famille.
        </p>
        <Link to={SITE_SIGNUP_URL} className="mkt-btn primary">
          Créer ma page et mon QR code
        </Link>
        <FreeNote />
      </figcaption>
    </figure>

    <section id="tester">
      <h2>Tester avant d’imprimer</h2>
      <ul className="mg-bullets">
        {TEST.map((tip) => (
          <li key={tip}>{tip}</li>
        ))}
      </ul>
    </section>

    <RelatedGuides items={relatedTo('qr')} />
  </GuideLayout>
);

export default QrGuidePage;
